// Mensagens enviadas por nós que não nasceram no chat de um chamado.
//
// O backend grava `Messages.ticketId = NULL` de propósito no envio de campanha
// ("salva somente no histórico por contato", queues.ts/sendCampaignMsg) e, em
// degradação, no agendamento que não conseguiu chamado. No histórico por
// contato essas linhas aparecem no meio das mensagens do chamado; o
// MessagesList precisa reconhecê-las para não desenhar um "Chamado aberto ...
// (Ticket #null)" nem atribuir a mensagem a quem está olhando a tela.

import { getMessageJsonData } from "./groupSenderName";
import { i18n } from "../translate/i18n";

export const hasNoTicket = (message) =>
  message?.ticketId === null || message?.ticketId === undefined;

export const hasSenderSign = (message) => {
  const sign = String(message?.sign || "").trim();
  return Boolean(sign) && sign !== "false" && sign !== "0";
};

// Rótulos lidos na hora (não em constante de módulo) para seguir o idioma.
export const getSentOriginLabel = (origin) =>
  i18n.t(`sentMessageOrigin.labels.${origin === "campaign" || origin === "schedule" ? origin : "external"}`);

// Marcador que o backend grava em PT como remetente de envio externo.
const BACKEND_EXTERNAL_MARKER = "mensagem enviada externamente";

/**
 * Origem de uma mensagem enviada fora do chat de um chamado:
 *   "campaign" — dataJson.source gravado pelo sendCampaignMsg (sem sign, sem user)
 *   "schedule" — agendamento; pelo caminho Baileys o dataJson é o WAMessage, então
 *                a pista é a assinatura do agendador numa linha sem chamado
 *   "external" — celular, API por token, etc.
 */
export const getSentMessageOrigin = (message) => {
  const data = getMessageJsonData(message);
  if (data?.source === "campaign") return "campaign";
  if (data?.source === "schedule") return "schedule";
  if (hasNoTicket(message) && message?.signedMessage && hasSenderSign(message)) {
    return "schedule";
  }
  return "external";
};

/**
 * Chip para uma linha sem chamado, no lugar do "Chamado aberto ...".
 * `formattedDate` já vem formatado por quem chama (mesmo formato dos chips de
 * abertura do chamado).
 */
export const getNoTicketChipLabel = (message, formattedDate) => {
  const origin = getSentMessageOrigin(message);
  return i18n.t(`sentMessageOrigin.noTicket.${origin}`, { date: formattedDate });
};

/**
 * Chamado com que uma mensagem deve ser comparada para decidir se abre um chip
 * de "chamado novo": o último `ticketId` não nulo antes de `index`. Pular as
 * linhas sem chamado evita dois chips falsos em volta de uma campanha no meio
 * do chamado (o "#null" na entrada e um "Chamado aberto" repetido na saída).
 * Devolve undefined quando não há mensagem com chamado antes.
 */
export const findPreviousTicketId = (messages, index) => {
  for (let i = index - 1; i >= 0; i -= 1) {
    const previous = messages[i];
    if (!hasNoTicket(previous)) return previous.ticketId;
  }
  return undefined;
};

/**
 * Nome a mostrar no cabeçalho "Nome - data" de uma bolha enviada.
 *
 * Só a mensagem ainda não persistida (otimista/pendente, sem id do servidor) é
 * garantidamente de quem está logado. Linha gravada sem assinatura nem
 * atendente veio de fora do chat — campanha, agendamento, celular, API — e
 * mostrar o nome do usuário logado fazia cada atendente ver a si mesmo como
 * remetente da campanha.
 */
export const getSentMessageUserName = (message, loggedUserName) => {
  const signedName = String(message?.sign || "").trim();
  const normalizedSignedName = signedName && signedName !== "false"
    ? signedName.replace(/^[*\s]+|[*\s]+$/g, "").replace(/:$/, "").trim()
    : "";

  // Mensagens antigas da API Oficial foram gravadas sem `sign`, com a
  // assinatura só no corpo ("*Nome:*\n..."). Sem ler daqui, caíam no dono
  // atual do chamado, que depois de uma transferência não é quem enviou.
  const bodySignedName = message?.fromMe && !normalizedSignedName
    ? (String(message?.body || "").match(/^\*([^*\n]{1,80}):\*\s*\r?\n/) || [])[1] || ""
    : "";

  const candidates = [
    message?.user?.name,
    message?.userName,
    normalizedSignedName,
    bodySignedName,
    message?.ticket?.user?.name,
  ];

  for (const candidate of candidates) {
    const name = String(candidate || "").trim();
    if (name.toLowerCase() === BACKEND_EXTERNAL_MARKER) return i18n.t("messagePreview.externalMessage");
    if (name) return name;
  }

  const isTransient = !message?.id && Boolean(message?.tempId || message?.optimisticId);
  if (isTransient) {
    const loggedName = String(loggedUserName || "").trim();
    if (loggedName) return loggedName;
  }

  return getSentOriginLabel(getSentMessageOrigin(message));
};
